import { Table, Tag, Typography } from "antd";
import { DateField } from "@refinedev/antd";
import {
  OTHER_SERVICE_LABELS,
  OTHER_SERVICE_PREFIXES,
  OtherServicePrefix,
  SERVICES_WITH_PAY_PER_USE_AMOUNT,
} from "./otherServicesMeta";
import { formatMoney } from "components/metadata/formatMoney";
import { formatEnrollmentDate } from "components/metadata/enrollmentDate";

const { Text } = Typography;

interface Props {
  others: Record<string, unknown> | null | undefined;
  createdAt?: unknown;
}

interface OtherServiceRow {
  key: OtherServicePrefix;
  product: string;
  service: string;
  subscriptionType: string;
  value: string;
  enrollmentDate: string;
  active: boolean;
}

const formatFee = (currency: string, amount: number): string =>
  formatMoney(currency, amount);

// Mirrors buildOtherServiceRow in emr-api render-subscription-details.ts
const buildRow = (
  prefix: OtherServicePrefix,
  others: Record<string, unknown>,
): OtherServiceRow => {
  const [product, service] = OTHER_SERVICE_LABELS[prefix];
  const rawStatus = others[`${prefix}Status`];
  const status = rawStatus == null || rawStatus === "" ? "" : String(rawStatus).toLowerCase();
  const active = status !== "" && status !== "inactive";

  if (!active) {
    return { key: prefix, product, service, subscriptionType: "", value: "", enrollmentDate: "", active };
  }

  const subscriptionType = String(others[`${prefix}SubscriptionType`] ?? "");
  const currency = String(others[`${prefix}Currency`] ?? "");

  let value: string;
  if (subscriptionType === "Pay Per Use") {
    if (SERVICES_WITH_PAY_PER_USE_AMOUNT.has(prefix)) {
      value = formatFee(currency, Number(others[`${prefix}PayPerUseAmount`]) || 0);
    } else {
      value = `${Number(others[`${prefix}DiscountPercentage`]) || 0}%`;
    }
  } else {
    value = formatFee(currency, Number(others[`${prefix}AnnualFlatFeePerVessel`]) || 0);
  }

  const enrollmentDate = formatEnrollmentDate(others[`${prefix}EnrollmentDate`]);

  return { key: prefix, product, service, subscriptionType, value, enrollmentDate, active };
};

const dash = <Text type="secondary" italic>—</Text>;

export const OtherServicesTable: React.FC<Props> = ({ others, createdAt }) => {
  if (!others) {
    return <Text type="secondary" italic>No other services subscribed</Text>;
  }

  const rows = OTHER_SERVICE_PREFIXES.map((prefix) => buildRow(prefix, others));

  return (
    <Table<OtherServiceRow>
      size="small"
      rowKey="key"
      pagination={false}
      dataSource={rows}
      columns={[
        {
          title: "Service",
          key: "service",
          render: (_, row) => (
            <>
              <Text strong>{row.product}</Text>
              <br />
              <Text type="secondary">{row.service}</Text>
            </>
          ),
        },
        {
          title: "Subscription Type",
          dataIndex: "subscriptionType",
          key: "subscriptionType",
          render: (v: string, row) => (row.active && v ? <Text>{v}</Text> : dash),
        },
        {
          title: "Annual Flat Fee / Discount",
          dataIndex: "value",
          key: "value",
          render: (v: string, row) => (row.active && v ? <Text>{v}</Text> : dash),
        },
        {
          title: "Enrollment Date",
          dataIndex: "enrollmentDate",
          key: "enrollmentDate",
          render: (v: string, row) => (row.active && v ? <Text>{v}</Text> : dash),
        },
        {
          title: "Created Date",
          key: "createdAt",
          render: (_, row) =>
            row.active && typeof createdAt === "string" && createdAt
              ? <DateField value={createdAt} format="DD-MMM-YYYY" />
              : dash,
        },
        {
          title: "Status",
          key: "status",
          render: (_, row) => (
            <Tag color={row.active ? "green" : "default"}>
              {row.active ? "active" : "inactive"}
            </Tag>
          ),
        },
      ]}
    />
  );
};
